import React, { useState, useEffect, useRef, useCallback } from "react";
import { LocalizationProvider, StaticDateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import MuiTextField from "@mui/material/TextField";
import Popover from "@mui/material/Popover";
import Box from "@mui/material/Box";
import { CalendarTodayOutlined, AccessTimeOutlined } from "@material-ui/icons";
import classNames from "classnames";
import moment from "moment";
import Button from "../button/button";
import locale from "localization";
import styles from "./date-time-picker-popup.module.scss";

const DEFAULT_DISPLAY_FORMAT = "MMMM D, YYYY  h:mm A";

// Mirrors timePickerPurple pattern from TimePickerV2 + parsedValue-specific additions
const pickerSx = {
  // ── Toolbar (purple header) ──────────────────────────────────────────────────
  ".MuiPickerStaticWrapper-content &.MuiPickersToolbar-root": {
    backgroundColor: "#5349b7 !important",
  },
  ".MuiPickerStaticWrapper-content &.css-7lip4c": {
    backgroundColor: "#5349b7 !important",
    background: "#5349b7 !important",
  },
  "& .MuiPickersToolbar-root": {
    backgroundColor: "#5349b7",
    color: "#fff",
  },
  // Year label (overline text above Date)
  "& .MuiPickersToolbar-root .MuiTypography-overline": {
    color: "#a79fff",
    opacity: 1,
  },
  // ── Date portion of toolbar (left side) ─────────────────────────────────────
  "& .MuiDateTimePickerToolbar-dateContainer .MuiTypography-root": {
    color: "#a79fff",
    fontWeight: 400,
  },
  "& .MuiDateTimePickerToolbar-dateContainer .MuiButtonBase-root": {
    background: "transparent !important",
    "& .Mui-selected": {
      color: "#ffffff !important",
      opacity: "1",
    },
  },
  "& .MuiDateTimePickerToolbar-dateContainer .Mui-selected": {
    color: "#ffffff !important",
    opacity: "1",
  },
  // ── Time portion of toolbar (right side) ─────────────────────────────────────
  "& .MuiDateTimePickerToolbar-timeContainer": {
    alignItems: "center",
    gap: "4px",
  },
  "& .MuiDateTimePickerToolbar-timeContainer .MuiButtonBase-root": {
    background: "transparent !important",
  },
  "& .MuiDateTimePickerToolbar-timeContainer .MuiTypography-root": {
    color: "#ffffff",
    opacity: 0.6,
    fontSize: "32px !important",
    fontWeight: 300,
    padding: "4px 8px",
  },
  "& .MuiDateTimePickerToolbar-timeContainer .Mui-selected": {
    color: "#ffffff !important",
    opacity: "1 !important",
    border: "1px solid rgba(255,255,255,0.5)",
    borderRadius: "8px",
  },
  "& .MuiTimePickerToolbar-separator": {
    color: "#a79fff !important",
    fontSize: "24px !important",
    opacity: 1,
  },
  // AM/PM toggle in toolbar
  "& .MuiTimePickerToolbar-ampmSelection": {
    "& .MuiTypography-root": {
      color: "#a79fff",
      fontSize: "12px !important",
    },
    "& .Mui-selected": {
      color: "#ffffff !important",
      border: "1px solid rgba(255,255,255,0.5)",
      borderRadius: "4px",
      backgroundColor: "rgba(255,255,255,0.15)",
      padding: "2px 6px !important",
    },
  },
  // ── Calendar/clock tabs row ───────────────────────────────────────────────────
  "& .MuiDateTimePickerTabs-root": {
    backgroundColor: "#5349b7",
    minHeight: "44px",
    "& .MuiTab-root": {
      color: "#a79fff",
      minHeight: "44px",
      minWidth: "50%",
    },
    "& .MuiTab-root.Mui-selected": {
      color: "#ffffff",
    },
    "& .MuiTabs-indicator": {
      backgroundColor: "#ffffff",
      height: "2px",
    },
  },
  // ── Calendar: selected day circle ────────────────────────────────────────────
  "& .MuiPickersDay-root.Mui-selected": {
    backgroundColor: "#5349b7 !important",
    color: "#fff !important",
  },
  "& .MuiPickersDay-root:focus.Mui-selected": {
    backgroundColor: "#5349b7 !important",
  },
  "& .MuiPickersDay-root:hover": {
    backgroundColor: "rgba(83,73,183,0.1)",
  },
  // Month/year header nav
  "& .MuiPickersCalendarHeader-label": {
    color: "#5349b7",
    fontWeight: 600,
  },
  "& .MuiIconButton-root": {
    color: "#5349b7",
  },
  // ── Clock: pointer & pin ──────────────────────────────────────────────────────
  "& .MuiClockPointer-root": { backgroundColor: "#5349b7" },
  "& .MuiClockPointer-thumb": {
    backgroundColor: "#5349b7",
    borderColor: "#5349b7",
  },
  "& .MuiClock-pin": { backgroundColor: "#5349b7" },
  "& .MuiClockNumber-root.Mui-selected": { backgroundColor: "#5349b7" },
};

const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

// Replaces only the time buttons in the toolbar with editable inputs.
// The parsedValue side, clock tabs, and clock itself are all untouched.
const DateTimeToolbar = ({ parsedValue, onChange, openView, setOpenView, minDateTime, maxDateTime }) => {
  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("00");
  const [period, setPeriod] = useState("AM");
  const hourInputRef = useRef();
  const minuteRef = useRef();
  const activeFieldRef = useRef(null); // tracks which input is focused for focus restore

  const syncFromDate = useCallback((d) => {
    if (!d) return;
    const h = d.getHours();
    const m = d.getMinutes();
    const isPM = h >= 12;
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    setHour(String(h12));
    setMinute(String(m).padStart(2, "0"));
    setPeriod(isPM ? "PM" : "AM");
  }, []);

  useEffect(() => {
    if (!parsedValue) return;
    const min = minDateTime ? new Date(minDateTime) : null;
    const max = maxDateTime ? new Date(maxDateTime) : null;
    if (min && new Date(parsedValue) < min) {
      const clamped = new Date(min);
      syncFromDate(clamped);
      onChange(clamped);
    } else if (max && new Date(parsedValue) > max) {
      const clamped = new Date(max);
      syncFromDate(clamped);
      onChange(clamped);
    } else {
      syncFromDate(parsedValue);
    }
  }, [parsedValue, minDateTime, maxDateTime, syncFromDate, onChange]);

  const pushChange = useCallback(
    (h, m, p) => {
      if (!parsedValue) return;
      const hNum = clamp(parseInt(h) || 12, 1, 12);
      const mNum = clamp(parseInt(m) || 0, 0, 59);
      let hours24 = hNum;
      if (p === "PM" && hNum !== 12) hours24 = hNum + 12;
      if (p === "AM" && hNum === 12) hours24 = 0;
      const d = new Date(parsedValue);
      d.setHours(hours24, mNum, 0, 0);

      const min = minDateTime ? new Date(minDateTime) : null;
      const max = maxDateTime ? new Date(maxDateTime) : null;
      if (min && d < min) {
        d.setTime(min.getTime());
        syncFromDate(d);
      } else if (max && d > max) {
        d.setTime(max.getTime());
        syncFromDate(d);
      }

      const restoreField = activeFieldRef.current;
      onChange(d);
      setTimeout(() => {
        if (restoreField === "hour") hourInputRef.current?.focus();
        else if (restoreField === "minute") minuteRef.current?.focus();
      }, 0);
    },
    [parsedValue, minDateTime, maxDateTime, onChange, syncFromDate]
  );

  const handleHourChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 2);
    if (!raw) {
      setHour("");
      return;
    }
    const num = parseInt(raw);
    if (raw.length === 1) {
      if (num >= 1 && num <= 9) {
        setHour(raw);
        if (num >= 2) {
          // auto-advance: we're leaving hour, safe to push
          minuteRef.current?.focus();
          minuteRef.current?.select();
          pushChange(raw, minute, period);
        }
      }
      return;
    }
    if (num >= 10 && num <= 12) {
      setHour(raw);
      // auto-advance: we're leaving hour, safe to push
      minuteRef.current?.focus();
      minuteRef.current?.select();
      pushChange(raw, minute, period);
    }
  };

  const handleHourKeyDown = (e) => {
    if (e.key === "ArrowUp") {
      const h = String(clamp((parseInt(hour) || 0) + 1, 1, 12));
      setHour(h);
    } else if (e.key === "ArrowDown") {
      const h = String(clamp((parseInt(hour) || 13) - 1, 1, 12));
      setHour(h);
    } else if (e.key === "Tab" || e.key === "Enter") {
      minuteRef.current?.focus();
      minuteRef.current?.select();
    }
  };

  const handleHourBlur = () => {
    let h = hour;
    if (!h || h === "0") {
      h = "12";
    }
    const num = parseInt(h);
    if (num < 1 || num > 12) {
      h = "12";
    }
    setHour(h);
    pushChange(h, minute, period);
  };

  const handleMinuteChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 2);
    if (!raw) {
      setMinute("");
      return;
    }
    const num = parseInt(raw);
    if (num >= 0 && num <= 59) setMinute(raw);
  };

  const handleMinuteKeyDown = (e) => {
    if (e.key === "ArrowUp") {
      setMinute(String(clamp((parseInt(minute) || 0) + 1, 0, 59)).padStart(2, "0"));
    } else if (e.key === "ArrowDown") {
      setMinute(String(clamp((parseInt(minute) || 1) - 1, 0, 59)).padStart(2, "0"));
    }
  };

  const handleMinuteBlur = () => {
    const m = parseInt(minute);
    const normalized = !isNaN(m) && m >= 0 && m <= 59 ? String(m).padStart(2, "0") : "00";
    setMinute(normalized);
    pushChange(hour, normalized, period);
  };

  const handlePeriodChange = (p) => {
    setPeriod(p);
    pushChange(hour, minute, p);
  };

  // AM is disabled when switching to AM would produce a past datetime
  const isAMDisabled = (() => {
    if (!minDateTime || !parsedValue) return false;
    const min = new Date(minDateTime);
    const test = new Date(parsedValue);
    const hNum = clamp(parseInt(hour) || 12, 1, 12);
    const mNum = clamp(parseInt(minute) || 0, 0, 59);
    test.setHours(hNum === 12 ? 0 : hNum, mNum, 0, 0);
    return test < min;
  })();

  // PM is disabled when switching to PM would exceed maxDateTime
  const isPMDisabled = (() => {
    if (!maxDateTime || !parsedValue) return false;
    const max = new Date(maxDateTime);
    const test = new Date(parsedValue);
    const hNum = clamp(parseInt(hour) || 12, 1, 12);
    const mNum = clamp(parseInt(minute) || 0, 0, 59);
    const hours24 = hNum === 12 ? 12 : hNum + 12;
    test.setHours(hours24, mNum, 0, 0);
    return test > max;
  })();

  return (
    <div className={styles.toolbar}>
      <span className={styles.toolbarYear}>
        {parsedValue ? moment(parsedValue).format("YYYY") : "----"}
      </span>
      <div className={styles.toolbarRow}>
        {/* Date — clicking switches to calendar view */}
        <button
          type="button"
          className={`${styles.toolbarDate} ${
            openView === "day" || openView === "month" || openView === "year"
              ? styles.toolbarDateSelected
              : ""
          }`}
          onClick={() => setOpenView("day")}
        >
          {parsedValue ? moment(parsedValue).format("MMM D") : "--- --"}
        </button>

        {/* Time inputs — replace the original time buttons */}
        <div className={styles.toolbarTimeInputs}>
          <input
            ref={hourInputRef}
            className={styles.toolbarTimeBox}
            value={hour}
            onChange={handleHourChange}
            onKeyDown={handleHourKeyDown}
            onBlur={() => {
              activeFieldRef.current = null;
              handleHourBlur();
            }}
            onFocus={(e) => {
              activeFieldRef.current = "hour";
              e.target.select();
              setOpenView?.("hours");
            }}
            maxLength={2}
            inputMode="numeric"
            placeholder="--"
          />
          <span className={styles.toolbarColon}>:</span>
          <input
            ref={minuteRef}
            className={styles.toolbarTimeBox}
            value={minute}
            onChange={handleMinuteChange}
            onKeyDown={handleMinuteKeyDown}
            onBlur={() => {
              activeFieldRef.current = null;
              handleMinuteBlur();
            }}
            onFocus={(e) => {
              activeFieldRef.current = "minute";
              e.target.select();
              setOpenView?.("minutes");
            }}
            maxLength={2}
            inputMode="numeric"
            placeholder="00"
          />
          <div className={styles.toolbarPeriodGroup}>
            <button
              type="button"
              disabled={isAMDisabled}
              className={`${styles.toolbarPeriodBtn} ${period === "AM" ? styles.toolbarPeriodSelected : ""} ${isAMDisabled ? styles.toolbarPeriodDisabled : ""}`}
              onClick={() => handlePeriodChange("AM")}
            >
              AM
            </button>
            <button
              type="button"
              disabled={isPMDisabled}
              className={`${styles.toolbarPeriodBtn} ${period === "PM" ? styles.toolbarPeriodSelected : ""} ${isPMDisabled ? styles.toolbarPeriodDisabled : ""}`}
              onClick={() => handlePeriodChange("PM")}
            >
              PM
            </button>
          </div>
        </div>
      </div>

      {/* Calendar / Clock tabs */}
      <div className={styles.toolbarTabs}>
        <button
          type="button"
          className={`${styles.toolbarTab} ${openView === "day" || openView === "year" || openView === "month" ? styles.toolbarTabSelected : ""}`}
          onClick={() => setOpenView("day")}
        >
          <CalendarTodayOutlined fontSize="small" />
        </button>
        <button
          type="button"
          className={`${styles.toolbarTab} ${openView === "hours" || openView === "minutes" ? styles.toolbarTabSelected : ""}`}
          onClick={() => setOpenView("hours")}
        >
          <AccessTimeOutlined fontSize="small" />
        </button>
      </div>
    </div>
  );
};

const DateTimePickerPopup = ({
  value,
  onChange,
  name,
  placeholder = "Select Date and time",
  disabled,
  className,
  minDateTime,
  maxDateTime,
  error,
  format,
  actions = { clear: true, cancel: true, ok: true },
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pickerValue, setPickerValue] = useState(value || null);

  useEffect(() => {
    setPickerValue(value || null);
  }, [value]);

  const displayText = value ? moment(value).format(format || DEFAULT_DISPLAY_FORMAT) : "";

  const handleOpen = (e) => {
    if (disabled) return;
    setPickerValue(value || new Date());
    setAnchorEl(e.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleOk = () => {
    onChange?.(name, { value: pickerValue });
    handleClose();
  };

  const handleClear = () => {
    onChange?.(name, { value: null });
    setPickerValue(null);
    handleClose();
  };

  return (
    <>
      <MuiTextField
        value={displayText}
        placeholder={placeholder}
        disabled={disabled}
        error={!!error}
        className={classNames(styles.input, className)}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <CalendarTodayOutlined className={styles.calendarIcon} onClick={handleOpen} />
          ),
        }}
        onClick={handleOpen}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: styles.popoverPaper }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <StaticDateTimePicker
            value={pickerValue}
            onChange={(newVal) => setPickerValue(newVal)}
            renderInput={(params) => <MuiTextField {...params} />}
            componentsProps={{ actionBar: { actions: [] } }}
            ToolbarComponent={(props) => {
              return <DateTimeToolbar {...props} maxDateTime={maxDateTime} />;
            }}
            minDateTime={minDateTime}
            maxDateTime={maxDateTime}
            sx={pickerSx}
            label=" "
          />
        </LocalizationProvider>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          padding="4px 8px 12px"
        >
          <Box>
            {actions?.clear && (
              <Button variant="text" className={styles.btnSky} onClick={handleClear}>
                <b>{locale.clear}</b>
              </Button>
            )}
          </Box>
          <Box display="flex">
            {actions?.cancel && (
              <Button variant="text" className={styles.btnSky} onClick={handleClose}>
                <b>{locale.cancel}</b>
              </Button>
            )}
            {actions?.ok && (
              <Button variant="text" className={styles.btnSky} onClick={handleOk}>
                <b>{locale.ok}</b>
              </Button>
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default DateTimePickerPopup;
